import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Star, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import HeroSlider from '../components/HeroSlider';
import PremiumFeatures from '../components/PremiumFeatures';
import ProductCategories from '../components/ProductCategories';
import InstagramFeed from '../components/InstagramFeed';
import { googleBusinessService, GoogleBusinessReview, GoogleBusinessResponse } from '../services/googleBusinessService';
import { useSiteConfig } from '../contexts/SiteConfigContext';

// WhatsApp Link Component
const WhatsAppLink: React.FC<{ className?: string; children: React.ReactNode }> = ({ className, children }) => {
  const { currentLanguage } = useLanguage();
  const { config } = useSiteConfig();

  // WhatsApp numbers based on language
  const whatsappNumbers = {
    en: config.phoneNumberTony, // Tony
    id: config.phoneNumberNila  // Nila
  };

  // Default messages based on language
  const defaultMessages = {
    en: config.whatsappMessageTonyEn,
    id: config.whatsappMessageNilaId
  };

  const phoneNumber = whatsappNumbers[currentLanguage as keyof typeof whatsappNumbers] || whatsappNumbers.en;
  const message = defaultMessages[currentLanguage as keyof typeof defaultMessages] || defaultMessages.en;
  const encodedMessage = encodeURIComponent(message);

  const handleClick = () => {
    // Track WhatsApp click
    if (window.gtag) {
      window.gtag('event', 'whatsapp_click', {
        event_category: 'Contact',
        event_label: `WhatsApp ${currentLanguage.toUpperCase()}`,
      });
    }
  };

  return (
    <a
      href={`https://wa.me/${phoneNumber.replace(/\D/g, '')}?text=${encodedMessage}`}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      className={className}
    >
      {children}
    </a>
  );
};

const Home: React.FC = () => {
  const { t } = useTranslation();
  const [reviews, setReviews] = useState<GoogleBusinessReview[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await googleBusinessService.getReviews();
        if (response && response.reviews) {
          setReviews(response.reviews);
        } else {
          console.error('Invalid reviews data received');
          setReviews([]); // Reset to empty array if invalid data
        }
      } catch (error) {
        console.error('Failed to fetch reviews:', error);
        setReviews([]); // Reset to empty array on error
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream-50 to-premium-50">
      {/* Hero Section with Slider */}
      <HeroSlider />

      {/* Google Reviews Section */}
      <section className="py-12 md:py-16 bg-rose-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-800 text-center mb-2">What Our Customers Say</h2>
          <p className="text-center text-gray-500 mb-10">Real reviews from our beloved customers.</p>
          
          {/* Reviews Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            {loading ? (
              // Loading skeleton
              [...Array(3)].map((_, index) => (
                <div key={index} className="bg-white p-6 rounded-lg shadow-md animate-pulse min-h-[220px]">
                  <div className="flex items-center mb-4">
                    <div className="w-24 h-24 bg-gray-200 rounded-full mr-4"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  </div>
                  <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                </div>
              ))
            ) : (
              reviews.slice(0, 3).map((review, index) => (
                <motion.div
                  key={review.reviewId}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="p-6 rounded-xl shadow-lg text-center flex flex-col justify-between min-h-[240px] bg-white"
                >
                  {/* Reviewer Profile */}
                  <div className="flex items-center justify-center mb-4">
                    {review.reviewer.profilePhotoUrl && (
                      <img
                        src={review.reviewer.profilePhotoUrl}
                        alt={review.reviewer.displayName}
                        className="w-24 h-24 rounded-full mr-3 border-2 border-white shadow-md object-cover"
                      />
                    )}
                    <div>
                      <h3 className="font-semibold text-gray-800 text-base">{review.reviewer.displayName}</h3>
                      <div className="flex items-center justify-center gap-1 mt-1">
                        {[...Array(review.starRating)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                        ))}
                      </div>
                    </div>
                  </div>
                  {/* Review Comment */}
                  {review.comment && (
                    <p className="text-gray-700 mb-4 italic text-sm line-clamp-4">
                      &ldquo;{review.comment}&rdquo;
                    </p>
                  )}
                  {/* Review Date */}
                  <span className="text-xs text-gray-500 mt-auto">
                    {googleBusinessService.formatReviewDate(review.createTime)}
                  </span>
                </motion.div>
              ))
            )}
          </div>
          {/* View More Reviews Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-4"
          >
            <a
              href="https://www.google.com/maps/place/TN+Cakes+Bali/@-8.6837973,115.1709839,17z/data=!4m8!3m7!1s0x2dd24724ada5a7f7:0x4aca823c3722187c!8m2!3d-8.6837973!4f115.1709839!9m1!1b1!16s%2Fg%2F11gbxbmhls?entry=ttu&g_ep=EgoyMDI1MDczMC4wIKXMDSoASAFQAw%3D%3D"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              View More Reviews
              <ExternalLink className="w-4 h-4" />
            </a>
          </motion.div>
        </div>
      </section>
      
      {/* Premium Features Section */}
      <PremiumFeatures />
      {/* Product Categories */}
      <ProductCategories />
      {/* Instagram Feed Section */}
      <InstagramFeed />
      {/* Final CTA Section */}
      <section className="py-12 md:py-16 bg-gradient-to-br from-burgundy-50 via-cream-50 to-premium-50">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 mb-6">
              {t('globalCTA.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-12 leading-relaxed">
              {t('globalCTA.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <div className="flex flex-col sm:flex-row gap-3 items-center">
                <Link
                  to="/inquiry"
                  className="btn-premium text-lg px-10 py-4"
                >
                  {t('globalCTA.orderNow')}
                </Link>
                <WhatsAppLink className="flex items-center justify-center gap-2 px-4 py-4 bg-green-500 hover:bg-green-600 text-white rounded-full transition-colors duration-300 shadow-lg hover:shadow-xl">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                  </svg>
                  <span className="text-sm font-medium">WhatsApp Us</span>
                </WhatsAppLink>
              </div>
              <Link
                to="/portfolio"
                className="btn-outline-premium text-lg px-10 py-4 bg-white text-[#b91c1c] border border-[#b91c1c] hover:bg-[#b91c1c] hover:text-white transition-colors duration-300"
              >
                {t('globalCTA.viewGallery')}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
